import { useState, useEffect } from 'react';
import { categories, type Category, type Product } from '@/mocks/categories';
import SEO from '@/components/feature/SEO';

export type { Category, Product };

export default function CategoryPage() {
  const [activeTab, setActiveTab] = useState(categories[0].id);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const activeCategory = categories.find((c) => c.id === activeTab) || categories[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setSelectedProduct(null);
  }, [activeTab]);

  return (
    <>
      <SEO
        title="产品中心"
        description="尊龙集团全系列存储产品矩阵：DRAM芯片、内存模组、嵌入式存储、系统解决方案。从芯片到模组，覆盖消费级到企业级全场景需求。"
        keywords="DRAM芯片,DDR5内存,内存模组,嵌入式存储,存储器,半导体产品,尊龙集团"
        canonicalPath="/category"
      />

      <main className="pt-16">
        {/* Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
            <p className="text-primary-600 text-sm font-medium tracking-wider mb-2">产品中心</p>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              全系列存储产品矩阵
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
              从DRAM芯片到系统级解决方案，尊龙集团以技术创新驱动产品迭代，为全球客户提供高性能、高可靠的存储器产品
            </p>
          </div>
        </section>

        {/* Tabs */}
        <section className="border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2 px-1">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`} />
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Products */}
        <section className="py-10 md:py-14 bg-background-50" data-product-shop>
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
                {activeCategory.name}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
                {activeCategory.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {activeCategory.products.map((product) => (
                <button
                  key={product.id}
                  onClick={() => setSelectedProduct(product)}
                  className="text-left group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200"
                >
                  <div className="h-48 md:h-56 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {product.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {product.specs.slice(0, 3).map((spec) => (
                        <span
                          key={spec}
                          className="text-xs bg-background-100 text-foreground-600 px-2 py-0.5 rounded whitespace-nowrap"
                        >
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Product Detail Modal */}
        {selectedProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedProduct(null)} />
            <div className="relative bg-background-50 rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto">
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-600 z-10"
              >
                <i className="ri-close-line" />
              </button>

              <div className="h-56 md:h-72 overflow-hidden">
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="p-5 md:p-6">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">
                  {selectedProduct.name}
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                  {selectedProduct.description}
                </p>

                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">技术规格</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedProduct.specs.map((spec) => (
                    <span
                      key={spec}
                      className="text-sm bg-primary-50 text-primary-700 px-3 py-1.5 rounded-full whitespace-nowrap"
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '产品中心 - 尊龙集团',
          description: activeCategory.description,
          url: 'https://www.zunlonggroup.com/category',
          isPartOf: {
            '@type': 'WebSite',
            name: '尊龙集团中国官方网站',
            url: 'https://www.zunlonggroup.com/',
          },
        })}
      </script>
    </>
  );
}